import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Tooltip } from 'react-tippy';
import css from './QuestionMark.css';
import withTranslate from '../../containers/widgets/withTranslate';
import Icon, { IconSizes, IconTypes, IconVariants } from '../foundations/Icon';

const QuestionMark = ({
    message = '',
    translate = () => {},
    contentStyle = {},
    containerClassName = '',
    iconClassName = '',
    interactive = false,
    tooltipClassName = '',
    iconVariant = IconVariants.LIGHT,
    iconSize = IconSizes.X_SMALL,
    iconName = 'question-line',
    position = 'top',
    keepInViewport = false,
}) => {
    const getMessage = () => {
        if (typeof message === 'string') {
            return translate(message);
        }
        return message;
    };

    return (
        <Tooltip
            html={<div style={contentStyle}>{getMessage()}</div>}
            position={position}
            inertia
            className={tooltipClassName}
            interactive={interactive}
            theme="light"
            size="big"
            popperOptions={{
                modifiers: {
                    // Off by default, as it has always been: inside a scrolling container, letting popper
                    // shift the tooltip moves it away from the icon it belongs to. `keepInViewport` opts in
                    // where the caller knows the tooltip can reach a window edge - without it popper never
                    // repositions, so a tooltip anchored near the right edge is drawn past it rather than
                    // pulled back. Width is not the issue: general.css caps every popper at 500px.
                    preventOverflow: {
                        enabled: keepInViewport,
                        boundariesElement: 'viewport',
                    },
                    // Left disabled either way: this hides the tooltip once its reference is clipped, which
                    // for a header inside a scrolling grid means it vanishes mid-hover.
                    hide: {
                        enabled: false,
                    },
                },
            }}
        >
            <span className={classNames(css.questionMarkContainer, containerClassName)}>
                <Icon
                    type={IconTypes.MINGCUTE}
                    name={iconName}
                    size={iconSize}
                    variant={iconVariant}
                    className={iconClassName}
                />
            </span>
        </Tooltip>
    );
};

QuestionMark.propTypes = {
    message: PropTypes.string,
    contentStyle: PropTypes.objectOf(PropTypes.any),
    translate: PropTypes.func,
    // https://github.com/tvkhoa/react-tippy:
    // Makes a tooltip interactive, i.e. will not close when the user hovers over or clicks on the tooltip.
    // This lets you create a popover (similar to Bootstrap) when used in conjunction with a click trigger.
    interactive: PropTypes.bool,
    iconClassName: PropTypes.string,
    containerClassName: PropTypes.string,
    tooltipClassName: PropTypes.string,
    iconVariant: PropTypes.string,
    iconSize: PropTypes.string,
    iconName: PropTypes.string,
    position: PropTypes.oneOf([
        'top',
        'top-start',
        'top-end',
        'bottom',
        'bottom-start',
        'bottom-end',
        'left',
        'left-start',
        'left-end',
        'right',
        'right-start',
        'right-end',
    ]),
    // Let popper shift the tooltip so it stays inside the viewport instead of running off the edge.
    keepInViewport: PropTypes.bool,
};

export default memo(withTranslate(QuestionMark));
